import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useState } from "react";
import AttachmentsIconOrange from "assets/img/discussions/attachment-1.svg?react";
import AttachmentsIconBlue from "assets/img/discussions/attachment-2.svg?react";
import DownloadIcon from "assets/img/discussions/download.svg?react";
import { DownloadAllButton } from "components/elements/DownloadAllButton";
import { XMarkIcon } from "components/icons/outline";
import { trpc } from "lib/trpc";
import {
    ClientCurrentUser,
    ClientDocument,
    DiscussionCreate,
    DiscussionCreateSchema,
} from "@compass/shared/types";

type DiscussionAttachmentsModalProps = {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    attachments: ClientDocument[];
};

export const DiscussionAttachmentsModal = ({
    isOpen,
    onClose,
    attachments,
}: DiscussionAttachmentsModalProps) => {
    const getDownloadUrl = trpc.userContent.getDownloadUrl.useMutation();

    const downloadDocument = async (fileId: string, type?: string | null) => {
        const { downloadUrl } = await getDownloadUrl.mutateAsync({ fileId, type });
        window.open(downloadUrl, "_blank");
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={onClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-6 shadow-lg">
                    <div className="flex items-center justify-between">
                        <Dialog.Title className="text-lg font-semibold">
                            Attachments in this discussion
                        </Dialog.Title>
                        <Dialog.Close asChild>
                            <X className="h-5 w-5 text-[#424242] hover:text-gray-700" />
                        </Dialog.Close>
                    </div>

                    <div className="mt-2 mb-4 h-px w-full bg-[#e0e0e0]" />

                    {/* ATTACHMENTS */}
                    <div className="space-y-4 mt-6">
                        {attachments.length > 0 ? (
                            attachments.map((attachment) => (
                                <div
                                    key={attachment.id}
                                    className="flex items-center justify-between rounded-md py-2"
                                >
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        {attachment.type?.includes("zip") ? (
                                            <AttachmentsIconOrange className="h-8 w-8 shrink-0" />
                                        ) : (
                                            <AttachmentsIconBlue className="h-8 w-8 shrink-0" />
                                        )}
                                        <span className="truncate text-sm text-gray-700">
                                            {attachment.displayName ||
                                                attachment.name ||
                                                "Unnamed file"}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() =>
                                            downloadDocument(attachment.id, attachment.type)
                                        }
                                        className="text-gray-400 hover:text-gray-600"
                                        aria-label="Download single"
                                    >
                                        <DownloadIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="text-sm text-gray-500">No attachments.</div>
                        )}
                    </div>

                    {/* DOWNLOAD ALL — one zip via the server (PRTL-1350) rather than
                        N window.open() calls, which popup blockers cut off after the first. */}
                    <div className="mt-6 flex justify-end">
                        <DownloadAllButton
                            fileIds={attachments.map((a) => a.id)}
                            archiveName="discussion-attachments"
                            className="rounded bg-gray-200 px-4 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                        />
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
